// Home feed (PRD §3.3): published posts newest-first, unread banner + badges,
// role-specific empty states, load-more paging. Media/love-from-feed come later.

import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../auth/AuthContext";
import { api, ApiError } from "../lib/api";
import type { FeedPost } from "../lib/types";
import { timeAgo } from "../lib/time";
import { AppHeader } from "../components/AppHeader";
import { Button } from "../components/Button";
import { InlineVideo } from "../components/InlineVideo";

export function Feed() {
  const { user, isAdminView } = useAuth();
  const navigate = useNavigate();
  // Respects the header's Public/Admin toggle: in "Public" preview, Lionel sees
  // exactly the friend experience (no admin FAB, friend-side copy).
  const isAdmin = isAdminView;

  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Friend welcome only — Messy gets his own welcome on the admin dashboard.
  const [showWelcome, setShowWelcome] = useState(user?.role === "friend" && !!user.isFirstLogin);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { posts, hasMore, unreadCount } = await api.feed(0);
      setPosts(posts);
      setHasMore(hasMore);
      setUnreadCount(unreadCount);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't load the feed.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function loadMore() {
    setLoadingMore(true);
    try {
      const { posts: more, hasMore } = await api.feed(posts.length);
      setPosts((prev) => [...prev, ...more]);
      setHasMore(hasMore);
    } catch {
      /* keep what we have */
    } finally {
      setLoadingMore(false);
    }
  }

  const firstUnread = posts.find((p) => p.unread);
  function jumpToUnread() {
    if (firstUnread) {
      document.getElementById(`post-${firstUnread.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }

  return (
    <div className="paper-grain min-h-dvh pb-24">
      <AppHeader />

      <main className="mx-auto w-full max-w-xl px-5 py-6">
        {/* First-login welcome (PRD §3.2 / §3.8). */}
        {showWelcome && user ? (
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="ink-block mb-6 rounded-2xl bg-citrus-wash/60 p-5"
          >
            <h2 className="font-display text-3xl text-ink">Welcome, {user.realName}! 🍊</h2>
            <p className="mt-2 text-sm leading-relaxed text-ink-soft">
              You can love ❤️ and comment on Messy's posts — your fun name ({user.handle}) is what
              others see. Tap your name up top to change your emoji or photo.
            </p>
            <button
              onClick={() => setShowWelcome(false)}
              className="stamp mt-4 text-xs text-ink-muted underline decoration-dotted underline-offset-2 hover:text-ink"
            >
              Got it
            </button>
          </motion.section>
        ) : null}

        {/* Unread banner (PRD §3.3) — only shown when there's actually something unread. */}
        {!loading && !error && unreadCount > 0 ? (
          <button
            onClick={jumpToUnread}
            className="mb-6 flex w-full rounded-xl border-2 border-citrus/40 bg-citrus/10 px-4 py-3 text-left"
          >
            <span className="font-semibold text-ink">
              🍊 {unreadCount} new {unreadCount === 1 ? "post" : "posts"} since your last visit
            </span>
          </button>
        ) : null}

        {loading ? (
          <div className="flex justify-center py-20">
            <span className="h-8 w-8 animate-spin rounded-full border-[3px] border-citrus/30 border-t-citrus" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center gap-4 py-16 text-center">
            <p className="text-ink-muted">{error}</p>
            <Button variant="ghost" onClick={() => void load()}>
              Try again
            </Button>
          </div>
        ) : posts.length === 0 ? (
          <EmptyFeed isAdmin={isAdmin} />
        ) : (
          <ul className="flex flex-col gap-4">
            {posts.map((post, i) => (
              <motion.li
                key={post.id}
                id={`post-${post.id}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.04, 0.3) }}
              >
                <PostCard post={post} onOpen={() => navigate(`/post/${post.id}`)} />
              </motion.li>
            ))}
          </ul>
        )}

        {hasMore ? (
          <div className="mt-8 flex justify-center">
            <Button variant="ghost" loading={loadingMore} onClick={() => void loadMore()}>
              Load more
            </Button>
          </div>
        ) : null}
      </main>

      {/* Admin quick-authoring FAB. */}
      {isAdmin ? (
        <Link
          to="/admin/posts/new"
          className="fixed bottom-6 right-6 z-30 flex items-center gap-2 rounded-full border-2 border-ink bg-citrus px-5 py-3 font-semibold text-paper shadow-[3px_3px_0_0_var(--color-ink)] transition-transform active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"
        >
          <span className="text-lg leading-none">＋</span> Add a post
        </Link>
      ) : null}
    </div>
  );
}

function PostCard({ post, onOpen }: { post: FeedPost; onOpen: () => void }) {
  const hasVideo = !!(post.videoUrl || post.videoStreamUid);
  return (
    // A <video controls> can't legally sit inside a <button>, so this card is a div
    // with button-like keyboard/click behavior instead of a real <button>.
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className="ink-block block w-full cursor-pointer overflow-hidden rounded-2xl bg-paper text-left transition-transform hover:-translate-y-0.5"
    >
      {hasVideo ? (
        <InlineVideo videoUrl={post.videoUrl} videoStreamUid={post.videoStreamUid} posterUrl={post.coverImageUrl} />
      ) : post.coverImageUrl ? (
        <div className="w-full overflow-hidden bg-paper-deep">
          <img
            src={post.coverImageUrl}
            alt=""
            className="block w-full"
            loading="lazy"
          />
        </div>
      ) : null}
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-2xl leading-tight text-ink">{post.title}</h3>
          {post.unread ? (
            <span className="stamp shrink-0 rounded-full bg-citrus px-2 py-0.5 text-[0.6rem] text-paper">
              new
            </span>
          ) : null}
        </div>
        {post.snippet ? <p className="mt-2 line-clamp-3 text-ink-soft">{post.snippet}</p> : null}
        <div className="mt-4 flex items-center gap-4 text-sm text-ink-muted">
          <span>{timeAgo(post.publishedAt)}</span>
          <span className={post.lovedByMe ? "text-love" : ""}>❤ {post.loveCount}</span>
          <span>💬 {post.commentCount}</span>
        </div>
      </div>
    </div>
  );
}

function EmptyFeed({ isAdmin }: { isAdmin: boolean }) {
  return (
    <section className="flex flex-col items-center rounded-2xl border-2 border-dashed border-ink/20 px-6 py-16 text-center">
      <span className="text-5xl" aria-hidden>
        🍊
      </span>
      {isAdmin ? (
        <>
          <p className="mt-4 font-display text-3xl text-ink">Nothing here yet</p>
          <p className="mt-1 max-w-xs text-sm text-ink-muted">
            Dala what you must — tap below to write your first dispatch.
          </p>
          <div className="mt-6">
            <Link
              to="/admin/posts/new"
              className="inline-flex items-center gap-2 rounded-xl border-2 border-ink bg-citrus px-5 py-3 font-semibold text-paper shadow-[3px_3px_0_0_var(--color-ink)]"
            >
              Add a post
            </Link>
          </div>
        </>
      ) : (
        <>
          <p className="mt-4 font-display text-3xl text-ink">Quiet on the farm</p>
          <p className="mt-1 max-w-xs text-sm text-ink-muted">
            Messy hasn't posted yet — check back soon.
          </p>
        </>
      )}
    </section>
  );
}
